import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { PageHeader } from "@/components/StatCard";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ChevronLeft, Save, Check, CalendarCheck2, X } from "lucide-react";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function RescheduleSettings() {
  const { user } = useAuth();
  const t = useT();
  usePageMeta(t("resch.meta_title"));
  const [hours, setHours] = useState("24");
  const [savedHours, setSavedHours] = useState(24);
  const [saving, setSaving] = useState(false);
  const [reqs, setReqs] = useState(null);
  const [busy, setBusy] = useState(null);
  const [rejectMsg, setRejectMsg] = useState({});

  const load = useCallback(async () => {
    try { const { data } = await api.get("/coach/reschedule-settings"); setHours(String(data?.reschedule_auto_min_hours ?? 24)); setSavedHours(Number(data?.reschedule_auto_min_hours ?? 24)); } catch { /* keep */ }
    try { const { data } = await api.get("/coach/reschedule-requests?status=pending"); setReqs(data?.requests || []); } catch { setReqs([]); }
  }, []);
  useEffect(() => { if (user?.role === "coach") load(); }, [user, load]);

  if (user && user.role !== "coach") return <Navigate to="/app/circles" replace />;

  const saveHours = async () => {
    const parsed = parseInt(hours || "0", 10);
    if (!Number.isFinite(parsed) || parsed < 0 || parsed > 336) { toast.error(t("resch.invalid")); return; }
    setSaving(true);
    try { await api.patch("/coach/reschedule-settings", { reschedule_auto_min_hours: parsed }); setSavedHours(parsed); toast.success(t("resch.saved")); }
    catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const approve = async (r, window) => {
    setBusy(r.id);
    try { await api.post(`/coach/reschedule-requests/${r.id}/approve`, { chosen_window: window }); toast.success(t("resch.approved")); await load(); }
    catch (err) { toast.error(apiError(err)); }
    finally { setBusy(null); }
  };
  const reject = async (r) => {
    setBusy(r.id);
    try { await api.post(`/coach/reschedule-requests/${r.id}/reject`, { message: (rejectMsg[r.id] || "").trim() || undefined }); toast.success(t("resch.rejected")); await load(); }
    catch (err) { toast.error(apiError(err)); }
    finally { setBusy(null); }
  };

  return (
    <div data-testid="coach-reschedule-settings-page">
      <Seo title={t("resch.seo")} noindex />
      <Link to="/app/coach/settings" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="resch-back">
        <ChevronLeft className="h-4 w-4" /> {t("cset.title")}
      </Link>
      <PageHeader eyebrow={t("resch.eyebrow")} title={t("resch.title")} subtitle={t("resch.subtitle")} />

      <div className="rounded-2xl border border-border bg-card p-4">
        <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("resch.window_label")}</label>
        <div className="flex items-center gap-2">
          <Input type="number" value={hours} onChange={(e) => setHours(e.target.value)} className="max-w-[120px]" data-testid="resch-hours" />
          <span className="text-sm text-muted-foreground">h</span>
          <Button className="gap-1.5 rounded-full" onClick={saveHours} disabled={saving || parseInt(hours || "0", 10) === savedHours} data-testid="resch-save"><Save className="h-4 w-4" /> {t("resch.save")}</Button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">{t("resch.window_hint")}</p>
      </div>

      <p className="mb-2 mt-6 font-display text-lg text-foreground">{t("resch.pending_title")}</p>
      {reqs === null ? (
        <div className="space-y-2">{[0, 1].map((i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}</div>
      ) : reqs.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-8 text-center" data-testid="resch-empty">
          <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-accent text-accent-foreground"><CalendarCheck2 className="h-5 w-5" /></div>
          <p className="mt-3 text-sm text-muted-foreground">{t("resch.pending_empty")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reqs.map((r) => (
            <div key={r.id} data-testid={`resch-req-${r.id}`} className="rounded-2xl border border-border bg-card p-4">
              <div className="flex items-center justify-between gap-2">
                <p className="font-semibold text-foreground">{r.client_name}</p>
                <span className="text-xs text-muted-foreground">{r.session_title}</span>
              </div>
              <p className="mt-0.5 text-xs text-muted-foreground">{t("resch.currently")}: {fmt(r.current_scheduled_at)}{r.duration_min ? ` · ${r.duration_min} min` : ""}</p>
              {r.no_auto_reason && <p className="mt-1 text-xs text-amber-600">{r.no_auto_reason === "too_close" ? t("resch.reason_too_close") : t("resch.reason_no_slot")}</p>}
              {r.reason && <p className="mt-1 text-xs italic text-muted-foreground">“{r.reason}”</p>}

              <p className="mb-1.5 mt-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("resch.client_windows")}</p>
              <div className="space-y-1.5">
                {(r.preferred_windows || []).map((w) => (
                  <div key={w} className="flex items-center justify-between rounded-xl border border-border bg-secondary/40 px-3 py-2">
                    <span className="text-sm text-foreground">{fmt(w)}</span>
                    <Button size="sm" className="h-8 gap-1 rounded-full" disabled={busy === r.id} onClick={() => approve(r, w)} data-testid={`resch-approve-${r.id}`}><Check className="h-3.5 w-3.5" /> {t("resch.approve")}</Button>
                  </div>
                ))}
              </div>

              <div className="mt-3">
                <Textarea className="min-h-[52px]" value={rejectMsg[r.id] || ""} onChange={(e) => setRejectMsg((p) => ({ ...p, [r.id]: e.target.value }))} placeholder={t("resch.reject_ph")} data-testid={`resch-reject-msg-${r.id}`} />
                <div className="mt-2 flex justify-end">
                  <Button size="sm" variant="outline" className="gap-1.5 rounded-full text-rose-600" disabled={busy === r.id} onClick={() => reject(r)} data-testid={`resch-reject-${r.id}`}><X className="h-3.5 w-3.5" /> {t("resch.reject")}</Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
